import React, { memo, useEffect, useState } from 'react';
import { DialogContent, Dialog, Grid, Typography, IconButton, TextField, makeStyles, Button, CircularProgress, MenuItem } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { DialogTitle, NumberFormatMask } from 'components';
import { GREY, GREY_6, PRIMARY_MOBILE, WHITE } from 'constants/colors';

import { format, parseISO } from 'date-fns';
import { dummyBank } from 'utils/dummy';
import axios from 'axios';
import { BANK_BASE_URL } from 'constants/url';

const useStyles = makeStyles({
  container: {
    marginTop: 10,
    columnGap: '1em'
  },

  iconLarge: {
    fontSize: '3.5em'
  },
  containerImage: {
    margin: '0.7em 0',
    rowGap: '0.7em'
  },
  uploadImage: {
    padding: '1em ',
    backgroundColor: GREY,
    border: '2px dashed ' + GREY_6
  },
  browser: {
    color: PRIMARY_MOBILE,
    cursor: 'pointer'
  },
  wrapperImage: {
    columnGap: '1em'
  },
  containerBoxImage: {
    position: 'relative'
  },
  image: {
    objectFit: 'contain',
    borderRadius: '0.5em',
    height: '4em'
  },
  closeImage: {
    position: 'absolute',
    top: 5,
    right: 5
  },
  closeIcon: {
    backgroundColor: 'rgba(32, 42, 61, 0.88)',
    borderRadius: '50%',
    cursor: 'pointer',
    color: WHITE
  }
});

interface Props {
  giro: GiroModalModel[];
  open: boolean;
  handleCloseCreate: () => void;
  handleAddGiro: (newGiro: GiroModalModel) => void;
  editingGiro?: GiroModalModel | null;
}

const GiroModal = ({ giro, open, handleCloseCreate, handleAddGiro, editingGiro = null }: Props) => {
  const classes = useStyles();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [banks, setBanks] = useState<BankModel[]>([dummyBank]);
  const [loadingBank, setLoadingBank] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [messageSnackBar, setMessageSnackBar] = useState<string>('');
  const [directionError, setDirectionError] = useState<string>('');
  const [giroDirectionId, setGiroDirectionId] = useState<number>(0);
  const [errorGiro, setErrorGiro] = useState<string>('');
  const [existingGiros, setExistingGiros] = useState<GiroModalModel[]>([]);

  const [currentGiro, setCurrentGiro] = useState<GiroModalModel>({
    idBank: 0,
    giroBank: '',
    giroNumber: '',
    giroDueDate: new Date(),
    giroAmount: 0
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.target) {
      const { name, value } = event.target;

      // Validasi untuk giroNumber
      if (name === 'giroNumber') {
        const isDuplicate = existingGiros.some(
          giro => giro.giroNumber === value && giro.id !== currentGiro.id && giro.giroNumber !== currentGiro.giroNumber
        );

        if (isDuplicate) {
          setErrorGiro('Nomor giro sudah digunakan. Silakan masukkan nomor lain.');
          return;
        } else {
          setErrorGiro('');
        }
      }

      // Update state currentGiro
      setCurrentGiro(prev => {
        if (name === 'giroDueDate') {
          return { ...prev, giroDueDate: parseISO(value) };
        }
        return { ...prev, [name]: value };
      });
    }
  };
  const handleSubmit = () => {
    if (!currentGiro.giroBank || !currentGiro.giroAmount) {
      return;
    }

    setIsSubmit(true);
    const adjustedGiro = {
      ...currentGiro,
      giroDueDate: new Date(currentGiro.giroDueDate.getTime() - currentGiro.giroDueDate.getTimezoneOffset() * 60000)
    };

    handleAddGiro(adjustedGiro);
    setExistingGiros(prev => [...prev, adjustedGiro]);

    // handleAddGiro(currentGiro);
    // setExistingGiros(prev => [...prev, currentGiro]);
    setCurrentGiro({ idBank: 0, giroBank: '', giroDueDate: new Date(), giroNumber: '', giroAmount: 0 });
    setGiroDirectionId(0);
    setIsSubmit(false);
    handleCloseCreate();
  };

  const handleSnackBar = (message: string): void => {
    setOpenSnackbar(true);
    setMessageSnackBar(message);
  };

  const fetchBanks = async () => {
    setLoadingBank(true);

    try {
      const { data } = await axios.get(BANK_BASE_URL);
      const filteredBanks = data.data.filter((bank: any) => bank.name !== 'CASH');
      setBanks(filteredBanks);
    } catch (error) {
      console.log('error', error);
      handleSnackBar('Load data bank telah gagal');
    } finally {
      setLoadingBank(false);
    }
  };

  React.useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    setExistingGiros(giro);
  }, [giro]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (editingGiro) {
      setCurrentGiro({
        id: editingGiro.id,
        idBank: editingGiro.idBank ?? 0,
        giroBank: editingGiro.giroBank ?? '',
        giroNumber: editingGiro.giroNumber ?? '',
        giroDueDate: new Date(editingGiro.giroDueDate),
        giroAmount: editingGiro.giroAmount
      });
      setGiroDirectionId(editingGiro.idBank ?? 0);
      setErrorGiro('');
      return;
    }

    setCurrentGiro({ idBank: 0, giroBank: '', giroDueDate: new Date(), giroNumber: '', giroAmount: 0 });
    setGiroDirectionId(0);
    setErrorGiro('');
  }, [editingGiro, open]);

  const handleChangeDirection = (id: number): void => {
    setDirectionError('');

    if (id) {
      const findBank = banks.find(bank => bank.id === id && bank.bankName !== 'CASH');

      if (findBank) {
        setCurrentGiro(prev => ({
          ...prev,
          idBank: findBank.id,
          giroBank: findBank.bankName
        }));
        setGiroDirectionId(findBank.id);
      } else {
        setDirectionError('Bank tidak valid atau tidak ditemukan');
        setCurrentGiro(prev => ({ ...prev, giroBank: '', giroNumber: '' }));
        setGiroDirectionId(0);
      }
    } else {
      setDirectionError('Pilih tujuan pencarian');
    }
  };

  const disabled = !currentGiro.giroBank || !currentGiro.giroAmount;

  return (
    <Dialog maxWidth={'sm'} disableBackdropClick={true} aria-labelledby='customized-dialog-title' open={open}>
      <DialogTitle>
        <Grid xs={12} container justify='space-between'>
          <Typography variant='h5'>{editingGiro ? 'Edit Giro' : 'Tambah Giro'}</Typography>
          <IconButton onClick={handleCloseCreate} size='small'>
            <Close />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container direction='row'>
          <Grid xs={12}>
            <TextField
              style={{ width: '100%' }}
              label='Pilih Giro Bank'
              id='directionSelect'
              select
              value={giroDirectionId}
              error={directionError !== ''}
              helperText={directionError}
              onChange={(e: any) => handleChangeDirection(e.target.value)}
            >
              <MenuItem value='0'>Pilih Bank</MenuItem>
              {banks.map(bank => (
                <MenuItem value={bank.id}>{bank.bankName}</MenuItem>
              ))}
            </TextField>

            <TextField
              id='giroDueDate'
              name='giroDueDate'
              required
              fullWidth
              type='date'
              label='Tanggal Jatuh Tempo Giro'
              value={format(currentGiro.giroDueDate, 'yyyy-MM-dd')}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true
              }}
            />
            <TextField
              id='giroNumber'
              name='giroNumber'
              required
              placeholder='cth. 123456789'
              fullWidth
              autoComplete='off'
              label='Nomor Giro'
              value={currentGiro.giroNumber}
              onChange={handleChange}
              error={!!errorGiro}
              helperText={errorGiro}
            />

            <TextField
              id='giroAmount'
              name='giroAmount'
              required
              fullWidth
              placeholder='Nominal giro'
              autoComplete='off'
              label='Jumlah'
              value={currentGiro.giroAmount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCurrentGiro(prev => ({ ...prev, giroAmount: +event.target.value }))}
              InputProps={{
                inputComponent: NumberFormatMask as any
              }}
            />
          </Grid>

          <Grid container item xs={12} justify='flex-end' className={classes.container}>
            <Button color='secondary' onClick={handleCloseCreate}>
              Batal
            </Button>
            <Button disabled={isSubmit || disabled} onClick={handleSubmit}>
              {isSubmit ? <CircularProgress color='primary' size={20} /> : 'Simpan'}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default memo(GiroModal);
